import { DeleteOutline, EditOutlined } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { IssueMarkdown } from '@/tracker/entities/issue';
import { UserAvatar } from '@/tracker/entities/user';
import { Comment } from '@/tracker/types';
import { ConfirmDialog } from '@/tracker/ui/ConfirmDialog';
import { formatDateTime } from '@/tracker/utils/formatDate';

import { formatCommentTime } from '../CommentList/formatCommentTime';
import { TextEditor } from '../TextEditor';
import { IssueCardStore } from '../../model/IssueCardStore';

import s from './CommentItem.module.scss';

type Props = {
  comment: Comment;
  store: IssueCardStore;
  /** Править может только автор, даже админ нет */
  canEdit: boolean;
  /** Удалять — автор или админ */
  canDelete: boolean;
};

const validate = (text: string) => (text.trim() ? null : 'Комментарий не может быть пустым');

/** Комментарий: аватар с линией-ниткой слева, правка и удаление по наведению, как в Яндекс Трекере */
const CommentItem: React.FC<Props> = observer(({ comment, store, canEdit, canDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const isEdited = comment.updateTime !== comment.createTime;

  return (
    <li className={s.comment}>
      <div className={s.comment__aside}>
        <UserAvatar name={comment.author.displayName} size={32} />
      </div>
      <div className={s.comment__main}>
        <div className={s.comment__header}>
          <span className={s.comment__author}>{comment.author.displayName}</span>
          <span aria-hidden>·</span>
          <time dateTime={comment.createTime} title={formatDateTime(comment.createTime)}>
            {formatCommentTime(comment.createTime)}
          </time>
          {isEdited && <span title={formatDateTime(comment.updateTime)}>(изменён)</span>}
          {!isEditing && (
            <span className={s.comment__actions}>
              {canEdit && (
                <IconButton
                  size="small"
                  className={s.comment__action}
                  aria-label="Изменить комментарий"
                  disabled={store.isSaving}
                  onClick={() => setIsEditing(true)}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              )}
              {canDelete && (
                <IconButton
                  size="small"
                  className={s.comment__action}
                  aria-label="Удалить комментарий"
                  disabled={store.isSaving}
                  onClick={() => setIsConfirmingDelete(true)}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              )}
            </span>
          )}
        </div>
        {isEditing ? (
          <TextEditor
            multiline
            minRows={3}
            initialValue={comment.text}
            ariaLabel="Текст комментария"
            validate={validate}
            onSave={(text) => store.updateComment({ id: comment.id, text: text.trim() })}
            onClose={() => setIsEditing(false)}
          />
        ) : (
          <IssueMarkdown text={comment.text} emptyText="" />
        )}
      </div>

      <AnimatePresence>
        {isConfirmingDelete && (
          <ConfirmDialog
            title="Удалить комментарий?"
            text="Комментарий пропадёт из задачи, вернуть его не получится."
            disabled={store.isSaving}
            onConfirm={async () => {
              if (await store.deleteComment(comment.id)) setIsConfirmingDelete(false);
            }}
            onClose={() => setIsConfirmingDelete(false)}
          />
        )}
      </AnimatePresence>
    </li>
  );
});

export { CommentItem };
